import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Delete, Edit, OpenInNew, Save } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useCreateComsDailyReportMutation,
  useDeleteComsDailyReportMutation,
  useGetComsDailyReportsQuery,
  useUpdateComsDailyReportMutation,
  ComsDailyReport,
} from "../../redux/comsApi";
import ConfirmDialog from "../common/ConfirmDialog";

const COMS_NAVY = "#123456";

interface Props {
  blockId: string;
}

const DailyReports: React.FC<Props> = ({ blockId }) => {
  const navigate = useNavigate();
  const {
    data: reports = [],
    isLoading,
    refetch,
  } = useGetComsDailyReportsQuery(blockId);
  const [createReport] = useCreateComsDailyReportMutation();
  const [deleteReport] = useDeleteComsDailyReportMutation();
  const [updateReport] = useUpdateComsDailyReportMutation();

  const today = new Date().toISOString().split("T")[0];
  const [reportDate, setReportDate] = useState(today);
  const [opening, setOpening] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  // Date editing state
  const [editingDateId, setEditingDateId] = useState<string | null>(null);
  const [editingDateValue, setEditingDateValue] = useState("");
  const [savingDate, setSavingDate] = useState(false);

  const handleOpen = async () => {
    setOpening(true);
    try {
      const report = await createReport({
        blockId,
        reportDate,
        activities: [],
        materials: [],
        labour: [],
        equipment: [],
      }).unwrap();
      navigate(`${report.id}`);
    } finally {
      setOpening(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId) {
      await deleteReport(deleteId);
      setDeleteId(null);
      refetch();
    }
  };

  const handleStartEditDate = (r: ComsDailyReport) => {
    setEditingDateId(r.id!);
    setEditingDateValue(r.reportDate);
  };

  const handleSaveDate = async () => {
    if (!editingDateId || !editingDateValue) return;
    setSavingDate(true);
    try {
      await updateReport({
        id: editingDateId,
        data: { reportDate: editingDateValue },
      }).unwrap();
      setEditingDateId(null);
      refetch();
    } finally {
      setSavingDate(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="h2"
        sx={{ color: COMS_NAVY, fontWeight: 600, mb: 3 }}
      >
        Daily Reports
      </Typography>

      {/* Open / create report */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Open editable daily report sheet
        </Typography>
        <Box display="flex" alignItems="center" gap={2} mt={1}>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Report date
            </Typography>
            <TextField
              type="date"
              size="small"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              sx={{ width: 180 }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={
              opening ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <OpenInNew />
              )
            }
            onClick={handleOpen}
            disabled={opening}
            sx={{
              bgcolor: COMS_NAVY,
              "&:hover": { bgcolor: "#1a4a7a" },
              mt: 2.5,
            }}
          >
            Open editable sheet
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" mt={1}>
          The report opens as an Excel-style editable sheet inside this app.
        </Typography>
      </Paper>

      {/* Reports list */}
      <Paper sx={{ borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ px: 3, pt: 3, pb: 1 }}>
          All reports
        </Typography>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : reports.length === 0 ? (
          <Box px={3} pb={3}>
            <Typography color="text.secondary">No reports yet.</Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r: ComsDailyReport) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    {editingDateId === r.id ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <TextField
                          type="date"
                          size="small"
                          value={editingDateValue}
                          onChange={(e) => setEditingDateValue(e.target.value)}
                          sx={{ width: 155 }}
                        />
                        <Tooltip title="Save date">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleSaveDate}
                            disabled={savingDate}
                          >
                            {savingDate ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Save fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Button
                          size="small"
                          onClick={() => setEditingDateId(null)}
                        >
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {r.reportDate}
                        <Tooltip title="Edit date (D1)">
                          <IconButton
                            size="small"
                            onClick={() => handleStartEditDate(r)}
                          >
                            <Edit sx={{ fontSize: 15, color: "#999" }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{r.notes || "â€”"}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<OpenInNew />}
                      onClick={() => navigate(`${r.id}`)}
                      sx={{ mr: 1 }}
                    >
                      Open sheet
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteId(r.id!)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete Report"
        message="Are you sure you want to delete this daily report? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  );
};

export default DailyReports;
